import { createFileRoute } from '@tanstack/react-router'

/**
 * Comprehensive Health Check Endpoint for Static Site
 *
 * Purpose: Detailed system monitoring for developers and administrators
 * Use Cases:
 * - Debugging application issues
 * - Monitoring client-side performance
 * - Checking feature flag status
 * - Verifying build information
 * - Static site deployment verification
 *
 * Access: Should be restricted to authorized users only
 */
export const Route = createFileRoute('/health')({
  component: HealthCheck,
})

function HealthCheck() {
  const healthStatus = {
    status: 'healthy',
    timestamp: new Date().toISOString(),
    version: import.meta.env.VITE_APP_VERSION || '0.0.2',
    environment: import.meta.env.VITE_APP_ENV || 'development',
    uptime: performance.now(),
    deployment: {
      type: 'static-site',
      host: 's3-cloudfront',
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
      cdn: 'cloudfront',
    },
    memory: {
      used: (performance as any).memory?.usedJSHeapSize || 0,
      total: (performance as any).memory?.totalJSHeapSize || 0,
      limit: (performance as any).memory?.jsHeapSizeLimit || 0,
    },
    checks: {
      newrelic: typeof window !== 'undefined' && !!window.NREUM ? 'healthy' : 'not_configured',
      cloudfront: 'healthy', // Would check CloudFront status via API
      s3: 'healthy', // Would check S3 bucket status via API
      client_performance: 'healthy', // Client-side performance checks
      browser_support: 'healthy', // Browser compatibility
    },
    performance: {
      page_load_time: performance.timing?.loadEventEnd - performance.timing?.navigationStart || 0,
      dom_content_loaded:
        performance.timing?.domContentLoadedEventEnd - performance.timing?.navigationStart || 0,
      first_paint: performance.getEntriesByType('paint')[0]?.startTime || 0,
      navigation_start: performance.timing?.navigationStart || 0,
    },
    // build info removed to simplify environment configuration
    features: {
      newrelic_enabled: import.meta.env.VITE_NEW_RELIC_ENABLED === 'true',
      debug_mode: import.meta.env.VITE_ENABLE_DEBUG_MODE === 'true',
      cloudfront_enabled: true,
      s3_hosting: true,
    },
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">Health Check</h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              healthStatus.status === 'healthy'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}
          >
            {healthStatus.status.toUpperCase()}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h2 className="text-xl font-semibold mb-3">Application Info</h2>
            <dl className="space-y-2">
              <div>
                <dt className="font-medium text-gray-600">Version</dt>
                <dd className="text-gray-900">{healthStatus.version}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Environment</dt>
                <dd className="text-gray-900">{healthStatus.environment}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Timestamp</dt>
                <dd className="text-gray-900">{healthStatus.timestamp}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Uptime</dt>
                <dd className="text-gray-900">{Math.round(healthStatus.uptime)}ms</dd>
              </div>
            </dl>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">System Checks</h2>
            <dl className="space-y-2">
              {Object.entries(healthStatus.checks).map(([key, value]) => (
                <div key={key}>
                  <dt className="font-medium text-gray-600 capitalize">{key.replace('_', ' ')}</dt>
                  <dd
                    className={`${
                      value === 'healthy'
                        ? 'text-green-600'
                        : value === 'not_configured'
                          ? 'text-yellow-600'
                          : 'text-red-600'
                    }`}
                  >
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <h2 className="text-xl font-semibold mb-3">Memory Usage</h2>
            <dl className="space-y-2">
              <div>
                <dt className="font-medium text-gray-600">Used Heap</dt>
                <dd className="text-gray-900">
                  {(healthStatus.memory.used / 1024 / 1024).toFixed(2)} MB
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Total Heap</dt>
                <dd className="text-gray-900">
                  {(healthStatus.memory.total / 1024 / 1024).toFixed(2)} MB
                </dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Heap Limit</dt>
                <dd className="text-gray-900">
                  {(healthStatus.memory.limit / 1024 / 1024).toFixed(2)} MB
                </dd>
              </div>
            </dl>
          </div>

          {/* Build info removed to simplify environment configuration */}
        </div>

        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-3">Features</h2>
          <dl className="space-y-2">
            <div>
              <dt className="font-medium text-gray-600">New Relic Enabled</dt>
              <dd
                className={
                  healthStatus.features.newrelic_enabled ? 'text-green-600' : 'text-red-600'
                }
              >
                {healthStatus.features.newrelic_enabled ? 'Yes' : 'No'}
              </dd>
            </div>
            <div>
              <dt className="font-medium text-gray-600">Debug Mode</dt>
              <dd
                className={healthStatus.features.debug_mode ? 'text-yellow-600' : 'text-green-600'}
              >
                {healthStatus.features.debug_mode ? 'Enabled' : 'Disabled'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <h2 className="text-xl font-semibold mb-3">Raw JSON</h2>
          <pre className="bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
            {JSON.stringify(healthStatus, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  )
}
